import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import translations from "./translations";

const LanguageContext = createContext(null);

// NOTE: localStorage is used here ONLY for UI language preference (non-sensitive).
// Sensitive data (auth tokens, user data) use httpOnly cookies managed by the backend.
const STORAGE_KEY = "app_ui_language";

// Detect browser language and return supported language code
function detectBrowserLanguage() {
  const browserLang = navigator.language || navigator.userLanguage || "en";
  const langCode = browserLang.split("-")[0].toLowerCase();
  // Map browser language to our supported languages
  if (translations[langCode]) {
    return langCode;
  }
  // Fallback to English
  return "en";
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    // Check localStorage for saved UI language preference (non-sensitive)
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && translations[saved]) {
      return saved;
    }
    // Fallback to browser language detection instead of null
    return detectBrowserLanguage();
  });

  const setLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
      localStorage.setItem(STORAGE_KEY, lang);
    }
  }, []);

  // Keep <html lang> in sync with UI language (prevents wrong auto-translate detection)
  useEffect(() => {
    if (language) document.documentElement.lang = language;
  }, [language]);

  const t = useCallback((key) => {
    if (!language) return key;
    return translations[language]?.[key] || translations["en"]?.[key] || key;
  }, [language]);

  // hasSelectedLanguage is true if user explicitly selected a language (saved in localStorage)
  const hasSelectedLanguage = !!localStorage.getItem(STORAGE_KEY);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    language, 
    setLanguage, 
    t, 
    hasSelectedLanguage
  }), [language, setLanguage, t, hasSelectedLanguage]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

export default LanguageContext;
